import { Row } from "@tanstack/react-table";

import AvatarText from "@/components/reusables/AvatarText";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { SELECTOR_ID } from "@/constants/global-constants";
import { ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import { ExtendedColumnDef } from "@/interfaces/globals";
import { IProduct } from "@/interfaces/tools.interface";
import { cn } from "@/lib/utils";

type UseProdutTableColumnsProps = {
  getActions: (row: Row<IProduct>) => ActionButtonDataTypes[];
  writePermissions: boolean;
  toggleViewOnSiteSheet: (
    value: React.SetStateAction<{
      isOpen: boolean;
    } | null>,
  ) => void;
};

export const useProductTableColumns = ({
  getActions,
  writePermissions,
  toggleViewOnSiteSheet,
}: UseProdutTableColumnsProps): ExtendedColumnDef<IProduct>[] => {
  const { set } = useQueryID(ToolsKeys.PRODUCT_ASSIGNMENT_ID);

  return [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: SELECTOR_ID,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
          className={cn(
            "hidden group-hover/header:block",
            table.getIsSomePageRowsSelected() && "block",
            table.getIsAllPageRowsSelected() && "!block",
          )}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
          onClick={(e) => e.stopPropagation()}
        />
      ),
      size: 12,
      enableSorting: false,
      enableHiding: false,
    },
    {
      map: {
        key: "Name",
        dataTransformer: (data) => data.name ?? "-",
      },
      accessorKey: "Name",
      header: "Name",
      cell: ({ row }): JSX.Element => {
        return (
          <AvatarText
            src={row?.original?.images[0]}
            title={`${row.original.name}`}
            name={`${row.original.name}`}
            classNames={{
              titleClassName: "line-clamp-1 text-wrap",
            }}
          />
        );
      },
      meta: {
        minWidth: 120,
        showRightBorder: true,
        classNameRow: "max-w-[320px] truncate ",
      },
    },
    {
      map: {
        key: "Quantity",
        dataTransformer: (data) => data.quantity.toString() ?? "-",
      },
      accessorKey: "Quantity",
      header: "Quantity",
      cell: ({ row }) => row.original?.quantity || "-",
      meta: {
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Available",
        dataTransformer: (data): string => {
          const available = data._count?.available;

          return available ? `${available}` : "-";
        },
      },
      accessorKey: "Available",
      header: "Available",
      cell: ({ row }) => row.original._count?.available || "-",
      meta: {
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Under Repair",
        dataTransformer: (data) => data._count?.repair?.toString() ?? "-",
      },
      accessorKey: "Under Repair",
      header: "Under Repair",
      cell: ({ row }) => row.original._count?.repair || "-",
      meta: {
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "On Site",
        dataTransformer: (data) => data?._count?.onsite?.toString() ?? "0",
      },
      accessorKey: "On Site",
      header: "on Site",
      cell: ({ row }): JSX.Element => {
        return (
          <Badge
            variant={"brightSkyBlue"}
            className='cursor-pointer'
            onClick={(e) => {
              e.stopPropagation();
              set(row.original.id);
              toggleViewOnSiteSheet({
                isOpen: true,
              });
            }}
          >
            {row.original?._count?.onsite?.toString() ?? 0}
          </Badge>
        );
      },
      meta: {
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Consumed",
        dataTransformer: (data) => data?._count?.consumed?.toString() ?? "0",
      },
      accessorKey: "Consumed",
      header: "Consumed",
      cell: ({ row }) => row.original?._count?.consumed || "-",
    },
    {
      id: "actions",
      hideColumn: !writePermissions,
      cell: ({ row }): React.JSX.Element => {
        return (
          <>
            <CustomDropdown data={getActions(row)} />
          </>
        );
      },
      meta: {
        classNameRow: "w-20",
      },
    },
  ];
};
